import emailjs from '@emailjs/browser';

// ==================== CONFIGURACIÓN DE EMAILJS ====================
const EMAILJS_SERVICE_ID = 'service_lbf850v';
const EMAILJS_TEMPLATE_ID = 'template_7mjbx31';
const EMAILJS_PUBLIC_KEY = 'ZNXy9tr4MSvD1sRdD';

// Inicializar EmailJS
emailjs.init(EMAILJS_PUBLIC_KEY);

// ==================== CONSTANTES DE TIPOS DE EVENTO ====================
export const EVENT_TYPES = {
  MEETING: 'meeting',
  TASK: 'task',
  REMINDER: 'reminder',
  DEADLINE: 'deadline',
  CALL: 'call',
  OTHER: 'other'
};

export const EVENT_TYPE_LABELS = {
  [EVENT_TYPES.MEETING]: 'Reunión',
  [EVENT_TYPES.TASK]: 'Tarea',
  [EVENT_TYPES.REMINDER]: 'Recordatorio',
  [EVENT_TYPES.DEADLINE]: 'Fecha límite',
  [EVENT_TYPES.CALL]: 'Llamada',
  [EVENT_TYPES.OTHER]: 'Otro'
};

export const EVENT_TYPE_COLORS = {
  [EVENT_TYPES.MEETING]: '#1976d2',
  [EVENT_TYPES.TASK]: '#2e7d32',
  [EVENT_TYPES.REMINDER]: '#ed6c02',
  [EVENT_TYPES.DEADLINE]: '#d32f2f',
  [EVENT_TYPES.CALL]: '#9c27b0',
  [EVENT_TYPES.OTHER]: '#757575'
};

// ==================== FUNCIÓN PRINCIPAL DE ENVÍO ====================
/**
 * Envía un email genérico usando EmailJS
 * @param {Object} params - Parámetros del email
 * @param {string} params.to_email - Email del destinatario
 * @param {string} params.to_name - Nombre del destinatario
 * @param {string} params.subject - Asunto del email
 * @param {string} params.message - Cuerpo del mensaje
 * @param {string} params.type - Tipo de recordatorio
 * @returns {Object} Resultado del envío
 */
const sendEmail = async (params) => {
  try {
    const templateParams = {
      to_email: params.to_email,
      to_name: params.to_name || 'Usuario',
      subject: params.subject,
      message: params.message,
      type: params.type || 'reminder',
      date: new Date().toLocaleDateString('es-ES', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
      time: new Date().toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit'
      })
    };

    const response = await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      templateParams
    );

    console.log('✅ Email enviado exitosamente:', {
      to: params.to_email,
      subject: params.subject,
      type: params.type,
      timestamp: new Date().toISOString()
    });

    return { success: true, response };
  } catch (error) {
    console.error('❌ Error al enviar email:', {
      to: params.to_email,
      subject: params.subject,
      error: error.message,
      timestamp: new Date().toISOString()
    });

    return { success: false, error: error.message };
  }
};

// ==================== UTILIDADES ====================
/**
 * Calcula el tiempo restante hasta una fecha
 * @param {string|Date} date - Fecha objetivo
 * @returns {string} Tiempo restante en formato legible
 */
const getTimeLeft = (date) => {
  const now = new Date();
  const target = new Date(date);
  const diffMs = target - now;
  
  // Si ya pasó la fecha
  if (diffMs <= 0) return 'AHORA';
  
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffMinutes < 60) {
    return diffMinutes <= 1 ? '1 minuto' : `${diffMinutes} minutos`;
  }
  
  if (diffHours < 24) {
    return diffHours === 1 ? '1 hora' : `${diffHours} horas`;
  }
  
  return diffDays === 1 ? '1 día' : `${diffDays} días`;
};

/**
 * Formatea una fecha para mostrar en emails
 * @param {string|Date} date - Fecha a formatear
 * @param {boolean} includeTime - Incluir hora
 * @returns {string} Fecha formateada
 */
const formatDate = (date, includeTime = true) => {
  const options = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  };
  
  if (includeTime) {
    options.hour = '2-digit';
    options.minute = '2-digit';
  }
  
  return new Date(date).toLocaleDateString('es-ES', options);
};

// ==================== RECORDATORIOS ESPECÍFICOS ====================
/**
 * Envía recordatorio de propuesta
 */
export const sendProposalReminder = async (proposal, user) => {
  const statusLabels = {
    draft: 'Borrador',
    sent: 'Enviada',
    accepted: 'Aceptada',
    rejected: 'Rechazada'
  };

  const message = `
    📋 RECORDATORIO DE PROPUESTA
    
    Título: ${proposal.title}
    Estado: ${statusLabels[proposal.status] || proposal.status}
    Cliente: ${proposal.clientName || 'No especificado'}
    Email cliente: ${proposal.clientEmail || 'No especificado'}
    ${proposal.reminderDate ? `Fecha recordatorio: ${formatDate(proposal.reminderDate)}` : ''}
    ${proposal.amount ? `Monto: $${proposal.amount.toLocaleString()}` : ''}
    
    ⚠️ Accede al sistema para revisar y dar seguimiento a esta propuesta.
  `;

  return sendEmail({
    to_email: user.email,
    to_name: user.displayName || user.email,
    subject: `📋 Recordatorio: Propuesta "${proposal.title}"`,
    message,
    type: 'PROPOSAL_REMINDER'
  });
};

/**
 * Envía recordatorio de requerimiento pendiente en proyecto
 */
export const sendRequirementReminder = async (project, user) => {
  const message = `
    📁 REQUERIMIENTO PENDIENTE
    
    Proyecto: ${project.name}
    Cliente: ${project.clientName || 'No especificado'}
    Fecha: ${formatDate(new Date())}
    
    ⚠️ Aún no se ha subido el archivo de requerimiento para este proyecto.
    
    Es necesario completar este paso para continuar con el proceso.
    Accede al sistema para subir el documento correspondiente.
  `;

  return sendEmail({
    to_email: user.email,
    to_name: user.displayName || user.email,
    subject: `⚠️ Requerimiento pendiente - Proyecto "${project.name}"`,
    message,
    type: 'REQUIREMENT_REMINDER'
  });
};

/**
 * Envía recordatorio de tarea próxima a vencer
 */
export const sendTaskDeadlineReminder = async (task, user) => {
  const timeLeft = getTimeLeft(task.dueDate);
  
  const priorityLabels = {
    low: '🟢 Baja',
    medium: '🟡 Media',
    high: '🔴 Alta',
    urgent: '⚡ Urgente'
  };

  const statusLabels = {
    pending: 'Pendiente',
    in_progress: 'En progreso',
    review: 'En revisión'
  };

  const message = `
    ⏰ TAREA POR VENCER
    
    Tarea: ${task.title}
    ${task.description ? `Descripción: ${task.description}` : ''}
    Prioridad: ${priorityLabels[task.priority] || task.priority}
    Estado: ${statusLabels[task.status] || task.status}
    Vencimiento: ${formatDate(task.dueDate)}
    Tiempo restante: ${timeLeft}
    
    ${task.projectName ? `Proyecto: ${task.projectName}` : ''}
    
    ⚠️ Accede al sistema para completar esta tarea antes de su vencimiento.
  `;

  return sendEmail({
    to_email: user.email,
    to_name: user.displayName || user.email,
    subject: `⏰ ${timeLeft} - Tarea: "${task.title}"`,
    message,
    type: 'TASK_DEADLINE'
  });
};

/**
 * Envía recordatorio de evento del calendario
 * @param {Object} event - Evento del calendario
 * @param {Object} user - Usuario destinatario
 * @param {string} reminderType - Tipo de recordatorio ('24h', '1h', '15min')
 */
export const sendEventReminder = async (event, user, reminderType = '24h') => {
  const reminderLabels = {
    '24h': '24 horas',
    '1h': '1 hora',
    '15min': '15 minutos',
    'start': 'Ahora'
  };
  
  const timeLabel = reminderLabels[reminderType] || reminderType;
  const eventTypeLabel = EVENT_TYPE_LABELS[event.eventType] || event.eventType;
  
  const message = `
    📅 RECORDATORIO DE EVENTO
    
    Evento: ${event.title}
    Tipo: ${eventTypeLabel}
    Fecha: ${formatDate(event.startDate)}
    ${event.endDate ? `Finaliza: ${formatDate(event.endDate)}` : ''}
    ${event.allDay ? '📆 Evento de día completo' : ''}
    ${event.description ? `\nDescripción:\n${event.description}` : ''}
    ${event.location ? `\nUbicación: ${event.location}` : ''}
    ${event.meetingLink ? `\nLink de reunión: ${event.meetingLink}` : ''}
    
    ⏰ Inicia en ${timeLabel}
    
    Accede al calendario para más detalles.
  `;

  return sendEmail({
    to_email: user.email,
    to_name: user.displayName || user.email,
    subject: `📅 ${timeLabel} - ${eventTypeLabel}: "${event.title}"`,
    message,
    type: 'EVENT_REMINDER'
  });
};

/**
 * Envía factura por email
 */
export const sendInvoiceEmail = async (clientEmail, invoiceNumber, clientName, additionalMessage = '') => {
  const message = `
    📄 FACTURA ${invoiceNumber || 'BORRADOR'}
    
    Cliente: ${clientName || 'Cliente'}
    Fecha: ${formatDate(new Date(), false)}
    
    Adjuntamos la factura correspondiente a nuestros servicios.
    
    ${additionalMessage ? `\nMensaje adicional:\n${additionalMessage}\n` : ''}
    
    Para cualquier consulta o aclaración, no dude en contactarnos.
    
    Gracias por confiar en nosotros.
  `;

  return sendEmail({
    to_email: clientEmail,
    to_name: clientName || 'Cliente',
    subject: `📄 Factura ${invoiceNumber || 'Borrador'}`,
    message,
    type: 'INVOICE'
  });
};

/**
 * Envía notificación de bienvenida
 */
export const sendWelcomeEmail = async (user, temporaryPassword = null) => {
  const message = `
    🎉 ¡BIENVENIDO/A AL SISTEMA!
    
    Hola ${user.displayName || 'Usuario'},
    
    Tu cuenta ha sido creada exitosamente.
    
    ${temporaryPassword ? `
    🔑 Tus credenciales de acceso:
    Email: ${user.email}
    Contraseña temporal: ${temporaryPassword}
    
    ⚠️ Por seguridad, te recomendamos cambiar tu contraseña al iniciar sesión.
    ` : 'Ya puedes acceder al sistema con tus credenciales.'}
    
    Si tienes alguna duda, contacta al administrador.
    
    ¡Gracias por unirte!
  `;

  return sendEmail({
    to_email: user.email,
    to_name: user.displayName || user.email,
    subject: '🎉 Bienvenido/a al Sistema',
    message,
    type: 'WELCOME'
  });
};

/**
 * Envía notificación de cambio de estado
 */
export const sendStatusChangeNotification = async (item, newStatus, user, itemType = 'propuesta') => {
  const statusLabels = {
    // Propuestas
    draft: 'Borrador',
    sent: 'Enviada',
    accepted: 'Aceptada',
    rejected: 'Rechazada',
    // Proyectos
    active: 'Activo',
    completed: 'Completado',
    on_hold: 'En espera',
    cancelled: 'Cancelado',
    // Tareas
    pending: 'Pendiente',
    in_progress: 'En progreso',
    review: 'En revisión'
  };

  const itemTypeLabels = {
    propuesta: 'Propuesta',
    proyecto: 'Proyecto',
    tarea: 'Tarea'
  };

  const message = `
    🔄 CAMBIO DE ESTADO
    
    ${itemTypeLabels[itemType] || itemType}: ${item.title || item.name}
    Nuevo estado: ${statusLabels[newStatus] || newStatus}
    Fecha: ${formatDate(new Date())}
    ${user?.displayName ? `Actualizado por: ${user.displayName}` : ''}
    
    Accede al sistema para más detalles.
  `;

  return sendEmail({
    to_email: user.email,
    to_name: user.displayName || user.email,
    subject: `🔄 ${itemTypeLabels[itemType] || itemType} "${item.title || item.name}" - ${statusLabels[newStatus] || newStatus}`,
    message,
    type: 'STATUS_CHANGE'
  });
};